import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { X } from "lucide-react";

import { postApi } from "../../api/api_instance";
import { API_URLS } from "../../api/apiUrls";
import PopupCard from "./PopupCard";

const ResetPasswordsemployee = ({ onCloseReset }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const orgId = useMemo(
    () => searchParams.get("orgId") || localStorage.getItem("orgId") || "",
    [searchParams],
  );

  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailSentPopup, setEmailSentPopup] = useState(false);
  const [emailNotSentPopup, setEmailNotSentPopup] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (orgId) {
      localStorage.setItem("orgId", orgId);
    }
  }, [orgId]);

  useEffect(() => {
    if (emailSentPopup) {
      const timer = setTimeout(() => {
        setEmailSentPopup(false);
        handleClose();
      }, 3000); // Auto close after 3 seconds
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailSentPopup]);

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const validateForm = () => {
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    if (errors.email) {
      setErrors((prev) => ({ ...prev, email: "" }));
    }
  };

  const handleClose = () => {
    if (typeof onCloseReset === "function") {
      onCloseReset();
      return;
    }

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    if (orgId) {
      navigate(`/employee/login/${orgId}`);
      return;
    }

    navigate("/organization/login");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const response = await postApi(
        {
          apiUrl: API_URLS.FORGOT_EMPLOYEE_PASSWORD,
          data: { email: email.trim() },
        },
        false,
      );

      if (response?.success) {
        setEmailSentPopup(true);
      } else {
        throw new Error(
          response?.message || "Failed to send employee reset email",
        );
      }
    } catch (error) {
      console.error("Employee reset email error:", error);
      toast.error(
        error?.message || "Failed to send reset email. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-[#f6f7f5] flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-[600px] rounded-2xl bg-white px-6 py-8 sm:px-10 shadow-[0_20px_60px_rgba(15,23,42,0.12)] border border-gray-100 relative">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
              aria-label="Close modal"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-[#06513D] tracking-wide">
              CARBURN
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Carbon Footprint & ESG Reporting
            </p>

            <h3 className="mt-8 text-3xl font-semibold text-[#06513D]">
              Forgot Password
            </h3>
            <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-gray-600">
              Enter your registered email address. We will send you a reset link
              to create a new employee password.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                htmlFor="email"
                className="block text-base font-semibold text-slate-700 mb-2"
              >
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className={`block w-full rounded-lg border px-4 py-3 text-base outline-none transition-colors placeholder:text-gray-400 ${
                  errors.email
                    ? "border-red-300 focus:border-red-500"
                    : "border-gray-300 focus:border-[#06513D]"
                }`}
                placeholder="Enter your email"
                value={email}
                onChange={handleEmailChange}
                disabled={isLoading}
              />
              {errors.email && (
                <p className="mt-2 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-lg bg-[#06513D] py-3.5 text-base font-semibold text-white shadow-sm transition-colors hover:bg-[#054634] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isLoading ? "Sending..." : "Send Reset Link"}
            </button>
          </form>
        </div>
      </div>

      {emailSentPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <PopupCard
            type="success"
            title="Success"
            message="Please check your inbox for the employee password reset link."
          />
        </div>
      )}
    </>
  );
};

export default ResetPasswordsemployee;
